import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, ArrowLeft, AlertCircle, Settings } from 'lucide-react';
import { getApiBaseUrl, httpWithAuth } from '../services/apiClient';
import { openCustomerPortal } from '../services/subscriptionService';

interface PricingPageProps {
  navigateTo: (page: string) => void;
  userEmail?: string | null;
  trialStatus?: TrialStatus | null;
  onUpgrade?: (planId: string) => void; // Made optional since we use forms now
  hasActiveSubscription?: boolean; // Track if user has active subscription
  subscriptionProvider?: 'stripe' | 'marketplace' | null;
}

interface TrialStatus {
  user_id: string;
  email: string;
  trial_active: boolean;
  days_remaining: number;
  requests_used: number;
  requests_limit: number;
  subscription_status: string;
  can_make_request: boolean;
  needs_upgrade: boolean;
}

interface PricingPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: string;
  priceId: string;
}

interface StripeConfig {
  publishableKey: string;
  basicPrice: string;
  businessPrice: string;
  unlimitedPrice: string;
  plans: PricingPlan[];
}

interface CheckoutSessionResponse {
  checkout_url: string;
}

const PricingPage: React.FC<PricingPageProps> = ({
  navigateTo,
  userEmail,
  trialStatus,
  hasActiveSubscription = false,
  subscriptionProvider = null,
}) => {
  const [config, setConfig] = useState<StripeConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  useEffect(() => {
    // Check for success/cancel messages in URL
    const query = new URLSearchParams(window.location.search);


    if (query.get('canceled')) {
      setMessage('⚠️ Subscription was canceled. You can try again when you\'re ready.');
    }

    // Fetch Stripe configuration from backend
    fetchStripeConfig();
  }, []);

  const fetchStripeConfig = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/subscription/config`);
      if (!response.ok) {
        throw new Error('Failed to fetch config');
      }
      const data = await response.json();
      setConfig(data);
    } catch (error) {
      console.error('Failed to fetch Stripe config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId: string) => {
    setLoading(true);
    try {
      const data = await httpWithAuth<CheckoutSessionResponse>('/api/subscription/create-checkout-session', {
        method: 'POST',
        body: JSON.stringify({ price_id: priceId })
      });

      // Redirect to Stripe checkout page
      window.location.href = data.checkout_url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      // 401 errors are now handled by global AuthInterceptor
      setMessage('❌ Failed to start checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price / 100);
  };

  const getPlanFeatures = (planId: string) => {
    const features: Record<string, string[]> = {
      basic: [
        'AI PowerPoint Generation',
        '328 Professional Templates (IT & Business)',
        'File Upload & Data Analysis',
        'Custom Brand Theme',
        '~26 presentations/month (400 slides)',
        'Email Support'
      ],
      business: [
        'Everything in Basic',
        '5-Agent AI Pipeline (Consulting Quality)',
        'Slide Modification & Iteration',
        '~66 presentations/month (1,000 slides)',
        'Email Support'
      ],
      unlimited: [
        'Everything in Business',
        'Beta access to new AI features',
        '~200 presentations/month (3,000 slides)',
        'Priority Support'
      ]
    };
    return features[planId] || [];
  };

  const renderTrialStatus = () => {
    if (!trialStatus) return null;

    if (trialStatus.subscription_status === 'active') {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <Check className="h-5 w-5 text-green-600 mr-2" />
            <div>
              <h3 className="text-green-800 font-medium">Active Subscription</h3>
              <p className="text-green-700 text-sm">You have an active paid plan. Enjoy unlimited access!</p>
            </div>
          </div>
        </div>
      );
    }

    if (trialStatus.needs_upgrade) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <div>
              <h3 className="text-red-800 font-medium">Trial Expired</h3>
              <p className="text-red-700 text-sm">
                Your free trial has ended. Please select a plan to continue using our service.
              </p>
            </div>
          </div>
        </div>
      );
    }

    if (trialStatus.trial_active) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-blue-600 mr-2" />
              <div>
                <h3 className="text-blue-800 font-medium">Free Trial Active</h3>
                <p className="text-blue-700 text-sm">
                  {trialStatus.days_remaining} days remaining • {trialStatus.requests_used}/{trialStatus.requests_limit} requests used
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-blue-600">{trialStatus.days_remaining}</div>
              <div className="text-xs text-blue-600">days left</div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  };

  // Remove the message popup - we handle success/cancel in parent component now
  if (message && message.includes('canceled')) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <div className="text-center">
            <p className="text-lg font-medium mb-4 text-gray-600">
              {message}
            </p>
            <button
              onClick={() => {
                setMessage('');
                // Clear URL params safely
                if (typeof window !== 'undefined' && window.history && typeof window.history.replaceState === 'function') {
                  try {
                    window.history.replaceState({}, document.title, window.location.pathname);
                  } catch (error) {
                    console.warn('Could not clean URL parameters:', error);
                  }
                }
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">Failed to load pricing information</p>
          {hasActiveSubscription && (
            <button
              onClick={() => navigateTo('chat')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Chat
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 overflow-y-auto">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {/* Only show back button if user has active subscription */}
            {hasActiveSubscription && (
              <button
                onClick={() => navigateTo('chat')}
                className="p-2 hover:bg-gray-100 rounded-md transition-colors"
                title="Back to Chat"
              >
                <ArrowLeft size={20} className="text-gray-600" />
              </button>
            )}
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Pricing Plans</h1>
              <p className="text-sm text-gray-600">
                {hasActiveSubscription
                  ? 'Manage your subscription or upgrade to a different plan'
                  : 'Choose a plan to start using Advisio'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto py-12 px-6">
        {/* Trial Status */}
        {renderTrialStatus()}

        {/* Hero Section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Professional PowerPoint Generation
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Transform your presentations with AI-powered slide creation. Choose a plan that
            fits your needs, with secure Stripe payments.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {config.plans.map((plan) => {
            const features = getPlanFeatures(plan.id);
            const isPopular = plan.id === 'business';
            const isRecommended = trialStatus?.needs_upgrade && plan.id === 'business';
            const isBasic = plan.id === 'basic';

            return (
              <div
                key={plan.id}
                className={`bg-white rounded-xl border-2 overflow-hidden transition-all hover:shadow-lg ${
                  isPopular
                    ? 'border-blue-600 shadow-lg'
                    : isBasic
                    ? 'border-green-600 shadow-lg'
                    : 'border-gray-200'
                }`}
              >
                {/* Badge Section - Always present to maintain consistent height */}
                {isBasic ? (
                  <div className="bg-green-600 text-white text-center py-2 text-sm font-medium">
                    Free Trial
                  </div>
                ) : isPopular ? (
                  <div className="bg-blue-600 text-white text-center py-2 text-sm font-medium">
                    Most Popular
                  </div>
                ) : isRecommended ? (
                  <div className="bg-green-600 text-white text-center py-2 text-sm font-medium">
                    RECOMMENDED
                  </div>
                ) : (
                  <div className="bg-transparent text-center py-2 text-sm font-medium invisible">
                    Spacer
                  </div>
                )}

                <div className="p-6">
                  {/* Plan Name */}
                  <h3 className="text-xl font-semibold text-gray-900 mb-2 text-center">
                    {plan.name}
                  </h3>

                  {/* Price */}
                  <div className="flex items-center justify-center mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      {formatPrice(plan.price)}
                    </span>
                    <span className="text-gray-600 ml-1">/{plan.interval}</span>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {features.map((feature, index) => (
                      <li key={index} className="flex items-center">
                        <Check className="h-4 w-4 text-green-500 mr-2 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Subscribe Button */}
                  {hasActiveSubscription ? (
                    subscriptionProvider === 'marketplace' ? (
                      <button
                        onClick={() => window.open('https://admin.microsoft.com', '_blank')}
                        className="w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-100 text-gray-900 hover:bg-gray-200"
                      >
                        Manage in M365 Admin Center
                        <Settings className="ml-2 h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => openCustomerPortal()}
                        disabled={loading}
                        className="w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:bg-gray-300"
                      >
                        {loading ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600"></div>
                        ) : (
                          <>
                            Manage Subscription
                            <Settings className="ml-2 h-4 w-4" />
                          </>
                        )}
                      </button>
                    )
                  ) : (
                    <button
                      onClick={() => handleSubscribe(plan.priceId)}
                      disabled={loading}
                      className={`w-full py-3 px-4 rounded-lg font-medium transition-colors flex items-center justify-center ${
                        isPopular || isRecommended
                          ? 'bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400'
                          : 'bg-gray-100 text-gray-900 hover:bg-gray-200 disabled:bg-gray-300'
                      }`}
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      ) : (
                        <>
                          {plan.id === 'basic' ? 'Start Free Trial' : 'Start Plan'}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-2xl font-semibold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">What happens after my trial ends?</h4>
              <p className="text-sm text-gray-600">
                Your account remains accessible in read-only mode. You can view previous work but cannot create new presentations until you upgrade.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">Can I change plans later?</h4>
              <p className="text-sm text-gray-600">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect at your next billing cycle.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">What's included in the free trial?</h4>
              <p className="text-sm text-gray-600">
                Full access to all features for 7 days with up to 10 presentation requests. Credit card required.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 border border-gray-200">
              <h4 className="font-medium text-gray-900 mb-2">What counts as one presentation?</h4>
              <p className="text-sm text-gray-600">
                One presentation typically consists of 10-20 slides. Each complete PowerPoint deck you generate counts as one presentation request.
              </p>
            </div>
          </div>
        </div>

        {/* Additional Info */}
        <div className="mt-12 text-center text-gray-600">
          <p className="mb-2">
            ✓ No setup fees &nbsp;&nbsp; ✓ Cancel anytime &nbsp;&nbsp; ✓ Secure payment by Stripe
          </p>
          <p className="text-sm">
            All prices in USD. Taxes may apply based on your location.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
